import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Calendar,
  Clock,
  User,
  Phone,
  FileText,
  Pill,
  TestTube,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ArrowLeft,
  Printer 
} from "lucide-react";
import { BlobProvider } from "@react-pdf/renderer";
import PrescriptionPDF from "./ClinicComponent/PrescriptionPDF";
import { useLazyGetFullPrescriptionDetailsQuery } from "../State/Api/AddPrescriptionApiSlice";

// helper to open a print window and auto-print
const openPrintWindow = (url) => {
  if (!url) return;
  const win = window.open("", "_blank");
  if (!win) return;
  win.document.write("<html><head><title>Prescription</title></head><body style='margin:0;'></body></html>");
  win.document.close();
  const script = win.document.createElement("script");
  script.type = "text/javascript";
  script.text = `
    const iframe = document.createElement('iframe');
    iframe.style.width='100%';
    iframe.style.height='100vh';
    iframe.style.border='0';
    iframe.src='${url}';
    iframe.onload = function () {
      setTimeout(() => {
        iframe.contentWindow && iframe.contentWindow.focus && iframe.contentWindow.focus();
        iframe.contentWindow && iframe.contentWindow.print && iframe.contentWindow.print();
      }, 300);
    };
    document.body.appendChild(iframe);
  `;
  win.document.body.appendChild(script);
};

const ClinicPrescriptionView = () => {
  const navigate = useNavigate();
  
  // Updated to get clinic ID from URL parameters instead of localStorage
  const { userClinicID, userUniqueID, appointmentID } = useParams();

  // ✅ All hooks at the top
  const [getPrescriptiondata, { data = {}, isFetching, isLoading, isError }] =
    useLazyGetFullPrescriptionDetailsQuery();

  useEffect(() => {
    if (userUniqueID && appointmentID && userClinicID) {
      getPrescriptiondata({
        userClinicID,
        userUniqueID,
        appointmentID,
      });
    }
  }, [userUniqueID, appointmentID, userClinicID, getPrescriptiondata]);

  // ✅ Now handle conditional UI AFTER hooks
  if (isLoading || isFetching) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 animate-pulse">
        <div className="max-w-9xl mx-auto space-y-6">
          {/* Back Button */}
          <div className="h-8 w-32 bg-gray-300 rounded-md"></div>

          {/* Header */}
          <div className="h-24 bg-gray-300 rounded-lg"></div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              <div className="h-64 bg-gray-200 rounded-lg"></div>
              <div className="h-64 bg-gray-200 rounded-lg"></div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-2 space-y-6">
              <div className="h-64 bg-gray-200 rounded-lg"></div>
              <div className="h-64 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Failed to load prescription details.</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
  };

  const getStatusColor = (status = "") => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPriorityColor = (priority = "") => {
    switch (priority.toLowerCase()) {
      case "urgent":
        return "bg-red-100 text-red-800 border-red-200";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "normal":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPriorityIcon = (priority = "") => {
    switch (priority.toLowerCase()) {
      case "urgent":
        return <AlertTriangle className="w-4 h-4" />;
      case "high":
        return <AlertCircle className="w-4 h-4" />;
      case "normal":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <CheckCircle className="w-4 h-4" />;
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading prescription details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-9xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate("/Clinicprescription")}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div
            className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500"
            style={{ background: "var(--gradient-dark-button, linear-gradient(135deg, #374151 0%, #111827 50%, #030712 100%))" }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2" style={{ fontSize: "24px" }}>Prescription Details</h1>
                <p className="text-gray-300">Prescription ID: #{data.prescriptionID}</p>
                <p className="text-gray-300">Clinic ID: {userClinicID}</p>
              </div>

              <div className="flex items-center space-x-4">
                <span className={`px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(data.status)}`}>
                  {data.status}
                </span>
                <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {data.prescriptionType}
                </span>

                {/* NEW: Print pill (outline, not filled) */}
                <BlobProvider document={<PrescriptionPDF data={data} />}>
                  {({ url, loading }) => (
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => openPrintWindow(url)}
                      className="px-4 py-2 rounded-full text-sm font-medium border border-white/30 text-white/90 hover:bg-white/10 transition-colors flex items-center space-x-2"
                      aria-label="Print prescription"
                      title={loading ? "Preparing PDF..." : "Print"}
                    >
                      <Printer className="w-4 h-4" />
                      <span>{loading ? "Preparing..." : "Print"}</span>
                    </button>
                  )}
                </BlobProvider>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Patient Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-blue-600">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Patient Information
                  </h2>
                  <p className="text-gray-600">ID: {data.patientIDUniqueID}</p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {data.patientFullName}
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">DOB:</span>
                    <p className="font-medium">{formatDate(data.dateofBirth)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <p className="font-medium">{data.status}</p>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <div className="flex items-center mb-2">
                    <Phone className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-sm text-gray-500">Contact:</span>
                  </div>
                  <p className="font-medium">{data.contactNumber}</p>
                  <p className="text-sm text-gray-600">
                    Emergency: {data.emergencyContactNumber}
                  </p>
                </div>
              </div>
            </div>

            {/* Prescription Info */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-5">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-green-600">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Prescription Info
                  </h2>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <span className="text-gray-500 text-sm">Prescribed By:</span>
                  <p className="font-medium">{data.prescribedBy}</p>
                </div>

                <div className="grid grid-cols-1 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Issue Date:</span>
                    <p className="font-medium flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(data.prescriptionDate)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Expiry Date:</span>
                    <p className="font-medium flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {formatDate(data.expiryDate)}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <span className="text-gray-500 text-sm">Diagnosis:</span>
                  <p className="font-medium">{data.diagnosis}</p>
                </div>

                {data.notesForPharmacist && (
                  <div className="pt-4 border-t">
                    <span className="text-gray-500 text-sm">
                      Notes for Pharmacist:
                    </span>
                    <p className="font-medium text-sm bg-yellow-50 p-3 rounded-lg mt-2">
                      {data.notesForPharmacist}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Medications & Tests */}
          <div className="lg:col-span-2 space-y-6">
            {/* Medications */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-purple-600">
                  <Pill className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Medications
                  </h2>
                  <p className="text-gray-600">
                    {data.medications?.length || 0} medications prescribed
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {data.medications?.map((med) => (
                  <div
                    key={med.medicationID}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {med.medicationName}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {med.route || "N/A"}
                        </p>
                      </div>
                      <div>
                        {med.allowRefills ? (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                            Refills: {med.numberOfRefills}
                          </span>
                        ) : (
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                            No Refills
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Dosage:</span>
                        <p className="font-medium">{med.dosage}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Frequency:</span>
                        <p className="font-medium">{med.frequency}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Duration:</span>
                        <p className="font-medium">
                          {med.duration} {med.durationUnit}
                        </p>
                      </div>
                    </div>

                    {med.specialInstructions && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <span className="text-blue-700 text-sm font-medium">
                          Instructions:{" "}
                        </span>
                        <span className="text-blue-600 text-sm">
                          {med.specialInstructions}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Test Recommendations */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-amber-600">
                  <TestTube className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Test Recommendations
                  </h2>
                  <p className="text-gray-600">
                    {data.testRecommendations?.length || 0} tests recommended
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {data.testRecommendations?.map((test) => (
                  <div
                    key={test.testRecommendationID}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">
                        {test.testName}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium border flex items-center ${getPriorityColor(
                          test.priority
                        )}`}
                      >
                        {getPriorityIcon(test.priority)}
                        <span className="ml-1">{test.priority}</span>
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Remarks: </span>
                      {test.remarks}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicPrescriptionView;
